import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Check, X, Loader2, ChevronDown, ChevronRight, GitCompare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Platformens statusser oversat. Nøglerne er dens, teksterne er vores.
const STATUS = {
  queued:       { tekst: 'I kø',            farve: 'text-th-text-muted' },
  processing:   { tekst: 'Læses',           farve: 'text-th-text-muted' },
  needs_review: { tekst: 'Til gennemsyn',   farve: 'text-amber-600' },
  approved:     { tekst: 'Godkendt',        farve: 'text-emerald-600' },
  delivered:    { tekst: 'Leveret',         farve: 'text-emerald-600' },
  rejected:     { tekst: 'Afvist',          farve: 'text-th-text-muted' },
  failed:       { tekst: 'Kunne ikke læses', farve: 'text-red-600' },
  afvist:       { tekst: 'Afvist af udlæseren', farve: 'text-red-600' },
  intet_bilag:  { tekst: 'Intet bilag at læse', farve: 'text-th-text-faint' },
  ikke_faktura: { tekst: 'Ikke sendt — ligner ikke et bilag', farve: 'text-th-text-muted' },
  fil_mangler:  { tekst: 'Filen mangler',   farve: 'text-red-600' },
};

const tid = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—'
    : d.toLocaleString('da-DK', { day: '2-digit', month: '2-digit',
                                 hour: '2-digit', minute: '2-digit' });
};

const sek = (n) => (n === null || n === undefined ? '—' : `${String(n).replace('.', ',')} s`);

// Beløb vises dansk og sendes som tal. "11.480,00" er ét tal, ikke tre.
const visTal = (v) =>
  v === null || v === undefined || v === '' ? '' :
  Number(v).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const laesTal = (s) => {
  if (s === null || s === undefined) return null;
  const rent = String(s).replace(/\s|\./g, '').replace(',', '.').trim();
  if (rent === '') return null;
  const n = Number(rent);
  return Number.isFinite(n) ? n : undefined;   // undefined = kunne ikke læses
};

// Kun det, brugeren faktisk har rørt, sendes med. Platformen merger rekursivt,
// så et urørt felt skal simpelthen ikke være i kroppen.
function byggeRettelser(oprindelig, redigeret) {
  const ud = {};
  for (const [sti, vaerdi] of Object.entries(redigeret)) {
    const [gruppe, felt] = sti.includes('.') ? sti.split('.') : [null, sti];
    const foer = gruppe ? (oprindelig?.[gruppe] || {})[felt] : oprindelig?.[felt];
    const erTal = ['total', 'vat', 'subtotal'].includes(felt);
    const ny = erTal ? laesTal(vaerdi) : (vaerdi === '' ? null : vaerdi);
    if (ny === undefined) return { fejl: felt };
    const uaendret = erTal ? Number(foer ?? NaN) === Number(ny ?? NaN)
                           : (foer ?? null) === (ny ?? null);
    if (uaendret) continue;
    if (gruppe) (ud[gruppe] = ud[gruppe] || {})[felt] = ny;
    else ud[felt] = ny;
  }
  return { data: ud };
}

function Felt({ etiket, sti, vaerdi, redigerbar, onSkift, bredde = '', suffiks = '' }) {
  return (
    <div className="flex items-baseline gap-3 py-1">
      <span className="text-xs text-th-text-faint w-28 shrink-0">{etiket}</span>
      {redigerbar ? (
        <>
          <input
            value={vaerdi ?? ''}
            onChange={(e) => onSkift(sti, e.target.value)}
            className={`bg-th-input border border-th-border rounded px-2 py-1 text-sm
                        text-th-text focus:border-th-border-hover outline-none ${bredde || 'flex-1 min-w-0'}`}
          />
          {suffiks && <span className="text-xs text-th-text-faint">{suffiks}</span>}
        </>
      ) : (
        <span className="text-sm text-th-text break-words">
          {vaerdi || <span className="text-th-text-faint">—</span>} {suffiks}
        </span>
      )}
    </div>
  );
}

const KILDETEKST = {
  xml: 'læst som XML — gratis, ingen model',
  skabelon: 'læst med kendt skabelon — gratis, ingen model',
  ai: 'læst med Claude (API)',
  claude: 'læst med Claude (API)',
  ingen: 'fandt ikke nok',
};

function Sammenligning({ svar, nyData, kanRette, onOvertag }) {
  const g = svar.gammel?.data || {};
  const meta = svar.gammel || {};
  const tider = svar.ny || {};
  const n = nyData || {};

  const pf = svar.paperflow || {};
  const p = pf.data || {};

  const raekker = [
    ['Leverandør', 'supplier.name', (n.supplier || {}).name, (g.supplier || {}).name, (p.supplier || {}).name],
    ['CVR', 'supplier.cvr', (n.supplier || {}).cvr, (g.supplier || {}).cvr, (p.supplier || {}).cvr],
    ['Fakturanr.', 'invoice_number', n.invoice_number, g.invoice_number, p.invoice_number],
    ['Fakturadato', 'invoice_date', n.invoice_date, g.invoice_date, p.invoice_date],
    ['Forfald', 'due_date', n.due_date, g.due_date, p.due_date],
    ['Ekskl. moms', 'subtotal', visTal(n.subtotal), visTal(g.subtotal), visTal(p.subtotal)],
    ['Moms', 'vat', visTal(n.vat), visTal(g.vat), visTal(p.vat)],
    ['I alt', 'total', visTal(n.total), visTal(g.total), visTal(p.total)],
    ['Varelinjer', null, String((n.line_items || []).length), String(g.antal_linjer ?? ''), String(p.antal_linjer ?? '')],
  ];

  // Paperflow svarer ikke med det samme. Linjen skal sige HVAD der sker, ikke
  // bare staa tom — ellers ser en scanning, der er i gang, ud som en, der
  // fejlede.
  const pfTekst = !pf.slaaet_til ? 'ikke sat op'
    : pf.fejl ? pf.fejl
    : pf.data ? 'færdig'
    : pf.status === 'queued' ? 'sendt — scanner, kan tage minutter'
    : pf.status ? `scanner (${pf.status})`
    : 'sendt';

  // Overskrifterne hoerer i tabellens egen <thead>. Stod de i et gitter
  // ovenover, flugtede de ikke med kolonnerne — tabellen har TRE (navn,
  // invoice-ai, gammel), og et gitter med to satte "Gammel udlaeser" over
  // invoice-ais tal. Man laeste altsaa et resultat som den forkerte laesers.
  const Hoved = ({ navn, undertekst, tidspunkt, sekunder, ekstra }) => (
    <>
      <span className="text-xs font-medium text-th-text block">{navn}</span>
      <span className="text-xs text-th-text-faint block font-normal">{undertekst}</span>
      <span className="text-xs text-th-text-muted block font-normal mt-0.5">
        udlæst {tid(tidspunkt)} · <span className="text-th-text">{sek(sekunder)}</span>
        {ekstra ? <span className="text-th-text-faint"> · {ekstra}</span> : null}
      </span>
    </>
  );

  // Fandt den gamle intet, er hver eneste raekke formelt en "uenighed". At
  // farve dem alle gule ville sige, at de to er uenige om ni ting — de er
  // enige om nul, fordi den ene ikke svarede.
  const gammelSvarede = !meta.fejl && meta.kilde && meta.kilde !== 'ingen';

  return (
    <div className="mt-2 border border-th-border rounded-md p-3 bg-th-body">
      {meta.fejl && (
        <p className="text-xs text-red-600 mb-2">Gammel udlæser: {meta.fejl}</p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="align-top text-left">
              <th className="pr-3 pb-2 w-24" />
              <th className="pr-3 pb-2 font-normal">
                <Hoved navn="invoice-ai" undertekst="lokal model (NuExtract3) på Mac'en"
                       tidspunkt={tider.tidspunkt} sekunder={tider.sekunder}
                       ekstra={tider.ventetid ? `ventede ${sek(tider.ventetid)} i kø` : null} />
              </th>
              <th className="pr-3 pb-2 font-normal">
                <Hoved navn="Gammel udlæser"
                       undertekst={(KILDETEKST[meta.kilde] || meta.kilde || '—')
                         + (meta.ocr_koert ? ' · OCR kørt' : '')}
                       tidspunkt={meta.tidspunkt} sekunder={meta.sekunder} />
              </th>
              <th className="pb-2 font-normal">
                <Hoved navn="Paperflow" undertekst={pfTekst}
                       tidspunkt={pf.tidspunkt} sekunder={pf.sekunder} />
              </th>
            </tr>
          </thead>
          <tbody>
            {raekker.map(([etiket, sti, nyV, glV, pfV]) => {
              const uenige = gammelSvarede && String(nyV ?? '') !== String(glV ?? '');
              // Paperflow markeres for sig: den kan vaere uenig, uden at de to
              // andre er det, og saa er det netop dét, man skal se.
              const pfUenig = pf.data && String(nyV ?? '') !== String(pfV ?? '')
                              && nyV && pfV;
              const kanOvertage = uenige && kanRette && sti && glV !== '' && glV != null;
              return (
                <tr key={etiket} className={`border-t border-th-border-light ${uenige ? 'bg-amber-50/60' : ''}`}>
                  <td className="py-1 pr-3 text-xs text-th-text-faint whitespace-nowrap align-top">{etiket}</td>
                  <td className="py-1 pr-3 text-th-text break-words align-top">
                    {nyV || <span className="text-th-text-faint">—</span>}
                  </td>
                  <td className="py-1 pr-3 text-th-text break-words align-top">
                    {glV || <span className="text-th-text-faint">—</span>}
                    {kanOvertage && (
                      <button type="button" onClick={() => onOvertag(sti, String(glV))}
                              title="Overtag denne værdi"
                              className="ml-2 text-xs text-amber-700 hover:underline whitespace-nowrap">
                        ← overtag
                      </button>
                    )}
                  </td>
                  <td className={`py-1 break-words align-top ${pfUenig ? 'text-amber-800 dark:text-amber-300' : 'text-th-text'}`}>
                    {pfV || <span className="text-th-text-faint">—</span>}
                    {pfUenig && kanRette && sti && (
                      <button type="button" onClick={() => onOvertag(sti, String(pfV))}
                              title="Overtag Paperflows værdi"
                              className="ml-2 text-xs text-amber-700 hover:underline whitespace-nowrap">
                        ← overtag
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Tallene maaler ikke helt det samme, og det skal staa der — ellers
          drager man den forkerte slutning af en forskel paa et par sekunder. */}
      <p className="text-xs text-th-text-faint mt-2">
        invoice-ais tid er målt på platformen (model alene). Den gamles er målt her
        og indeholder både netværk og model. Paperflows er fra bilaget blev sendt,
        til svaret lå — de har et menneske i løkken, så den tæller ventetid med.
      </p>
    </div>
  );
}

export default function Udlaesning({ item }) {
  const [u, setU] = useState(null);          // hele udtrækket fra serveren
  const [redigeret, setRedigeret] = useState({});
  const [henter, setHenter] = useState(false);
  const [arbejder, setArbejder] = useState('');
  const [visLinjer, setVisLinjer] = useState(false);
  const [gammel, setGammel] = useState(null);      // den gamle udlaesers bud

  const harUdlaesning = !!(item.fa_status || item.fa_har_data);

  const hent = React.useCallback(() => {
    setHenter(true);
    return axios.get(`${API}/inbox/${item.id}/udlaesning`)
      .then(r => { setU(r.data); setRedigeret({}); })
      .catch(() => setU(null))
      .finally(() => setHenter(false));
  }, [item.id]);

  useEffect(() => { if (harUdlaesning) hent(); }, [harUdlaesning, hent]);

  if (!harUdlaesning) return null;
  if (henter && !u) {
    return (
      <div className="flex items-center gap-2 text-xs text-th-text-faint py-3">
        <Loader2 size={13} className="animate-spin" /> Henter udlæsning …
      </div>
    );
  }
  if (!u) return null;

  const status = STATUS[u.fa_status] || { tekst: u.fa_status || 'Ukendt', farve: 'text-th-text-muted' };
  const d = u.fa_data || {};
  const kanRette = u.fa_status === 'needs_review';
  const issues = u.fa_issues || [];
  const harRettelser = Object.keys(redigeret).length > 0;

  const v = (sti) => {
    if (sti in redigeret) return redigeret[sti];
    const [g, f] = sti.includes('.') ? sti.split('.') : [null, sti];
    const raa = g ? (d[g] || {})[f] : d[f];
    return ['total', 'vat', 'subtotal'].includes(f) ? visTal(raa) : (raa ?? '');
  };
  const skift = (sti, vaerdi) => setRedigeret(p => ({ ...p, [sti]: vaerdi }));

  const haandterKonflikt = (e) => {
    const detalje = e?.response?.data?.detail;
    if (e?.response?.status === 409 && detalje?.kode === 'VERSION_KONFLIKT') {
      toast.error(detalje.besked || 'Nogen har rettet i mellemtiden.');
      hent();
      return true;
    }
    return false;
  };

  const gem = async () => {
    const { data, fejl } = byggeRettelser(d, redigeret);
    if (fejl) { toast.error(`"${fejl}" kunne ikke læses som et tal`); return; }
    if (!Object.keys(data).length) { toast.info('Ingen ændringer at gemme'); return; }
    setArbejder('gem');
    try {
      const r = await axios.post(`${API}/inbox/${item.id}/udlaesning/rettelser`,
                                 { data, note: 'Rettet i indbakken' });
      setU(p => ({ ...p, fa_data: r.data.data, fa_issues: r.data.issues,
                   fa_confidence: r.data.confidence, fa_version: r.data.version }));
      setRedigeret({});
      toast.success(`Rettelser gemt (version ${r.data.version})`);
    } catch (e) {
      if (!haandterKonflikt(e)) toast.error('Rettelserne kunne ikke gemmes');
    } finally { setArbejder(''); }
  };

  const godkend = async () => {
    if (harRettelser && !window.confirm(
      'Du har rettelser, der ikke er gemt. De bliver IKKE leveret. Godkend alligevel?')) return;
    setArbejder('godkend');
    try {
      const r = await axios.post(`${API}/inbox/${item.id}/udlaesning/godkend`, { note: '' });
      toast.success(r.data.aendret
        ? `Godkendt — ${r.data.leveringer?.length || 0} levering(er) sat i gang`
        : 'Var allerede godkendt');
      hent();
    } catch (e) {
      if (!haandterKonflikt(e)) toast.error('Kunne ikke godkendes');
    } finally { setArbejder(''); }
  };

  // Den gamle udlaeser kalder Anthropics API pr. dokument og koster derfor
  // penge, hvor invoice-ai koerer lokalt. Den spoerges kun, naar nogen beder
  // om det — aldrig af sig selv, naar en sag foldes ud.
  const sammenlign = async () => {
    setArbejder('sammenlign');
    try {
      const r = await axios.get(`${API}/inbox/${item.id}/udlaesning/sammenlign`);
      setGammel(r.data);
      if (r.data?.fejl) toast.error(`Den gamle udlæser: ${r.data.fejl}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Den gamle udlæser svarer ikke');
    } finally { setArbejder(''); }
  };

  // Leverandoer og kunde byttes om i ét greb. Begge objekter sendes hele,
  // fordi platformen merger rekursivt: sendte vi kun navn og CVR, ville
  // adresse og momsnummer blive staaende fra den forkerte part.
  const bytOm = async () => {
    const lev = d.supplier || {};
    const kunde = d.customer || {};
    if (!lev.name && !kunde.name) { toast.error('Der er intet at bytte om'); return; }
    setArbejder('byt');
    try {
      const r = await axios.post(`${API}/inbox/${item.id}/udlaesning/rettelser`,
        { data: { supplier: kunde, customer: lev },
          note: 'Leverandør og kunde byttet om — dokumentet er stilet til dette selskab' });
      setU(p => ({ ...p, fa_data: r.data.data, fa_issues: r.data.issues,
                   fa_confidence: r.data.confidence, fa_version: r.data.version,
                   fa_vendt_om: false }));
      setRedigeret({});
      toast.success('Byttet om');
    } catch (e) {
      if (!haandterKonflikt(e)) toast.error('Kunne ikke byttes om');
    } finally { setArbejder(''); }
  };

  const sendAlligevel = async () => {
    setArbejder('send');
    try {
      await axios.post(`${API}/inbox/${item.id}/udlaesning/send`);
      toast.success('Sendt til udlæsning');
      hent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunne ikke sendes');
    } finally { setArbejder(''); }
  };

  const afvis = async () => {
    const grund = window.prompt('Hvorfor afvises den? (begrundelsen gemmes)');
    if (!grund || !grund.trim()) return;
    setArbejder('afvis');
    try {
      await axios.post(`${API}/inbox/${item.id}/udlaesning/afvis`, { begrundelse: grund.trim() });
      toast.success('Afvist');
      hent();
    } catch { toast.error('Kunne ikke afvises'); }
    finally { setArbejder(''); }
  };

  return (
    <div className="border border-th-border rounded-lg p-3 sm:p-4 mb-3 bg-th-card">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-th-text">Udlæst faktura</span>
          <span className={`text-xs ${status.farve}`}>{status.tekst}</span>
          {u.fa_confidence != null && (
            <span className="text-xs text-th-text-faint">
              sikkerhed {Math.round(u.fa_confidence * 100)} %
            </span>
          )}
        </div>
        {u.fa_version && <span className="text-xs text-th-text-faint">version {u.fa_version}</span>}
      </div>

      {u.fa_fejl && (
        <p className="text-xs text-red-600 mb-2">Udlæsningen afviste bilaget: {u.fa_fejl}</p>
      )}

      {/* Et nej fra tjekket skal kunne ses og omgøres. Skjulte fravalg er
          det samme som tabte bilag — bare uden at nogen opdager det. */}
      {u.fa_status === 'ikke_faktura' && (
        <div className="flex items-start gap-2 flex-wrap">
          <p className="text-xs text-th-text-secondary flex-1 min-w-0">
            {u.fa_fil ? <span className="text-th-text-faint">{u.fa_fil}: </span> : null}
            {u.fa_tjek_grund || 'ligner ikke et bilag'}. Derfor er det ikke sendt til udlæsning.
          </p>
          <Button size="sm" variant="outline" onClick={sendAlligevel} disabled={!!arbejder}>
            {arbejder === 'send' ? <Loader2 size={13} className="animate-spin mr-1" /> : null}
            Send alligevel
          </Button>
        </div>
      )}

      {/* Vores egen kontrol, ikke platformens: den kender ikke selskabet bag
          indbakken og kan derfor ikke se, at parterne staar omvendt. */}
      {u.fa_vendt_om && (
        <div className="flex items-start gap-2 flex-wrap mb-2 p-2 rounded border border-amber-300 bg-amber-50">
          <AlertTriangle size={14} className="text-amber-700 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-900 flex-1 min-w-0">
            Leverandøren har <strong>dette selskabs eget CVR</strong>. Dokumentet er stilet
            til selskabet, så leverandør og kunde ser byttet om.
          </p>
          {kanRette && (
            <Button size="sm" variant="outline" onClick={bytOm} disabled={!!arbejder}>
              {arbejder === 'byt' ? <Loader2 size={13} className="animate-spin mr-1" /> : null}
              Byt om
            </Button>
          )}
        </div>
      )}

      {Object.keys(d).length > 0 && (
        <div className="grid sm:grid-cols-2 gap-x-6">
          <div>
            <Felt etiket="Leverandør"  sti="supplier.name"   vaerdi={v('supplier.name')}   redigerbar={kanRette} onSkift={skift} />
            <Felt etiket="CVR"         sti="supplier.cvr"    vaerdi={v('supplier.cvr')}    redigerbar={kanRette} onSkift={skift} bredde="w-32" />
            <Felt etiket="Fakturanr."  sti="invoice_number"  vaerdi={v('invoice_number')}  redigerbar={kanRette} onSkift={skift} />
            <Felt etiket="Fakturadato" sti="invoice_date"    vaerdi={v('invoice_date')}    redigerbar={kanRette} onSkift={skift} bredde="w-36" />
            <Felt etiket="Forfald"     sti="due_date"        vaerdi={v('due_date')}        redigerbar={kanRette} onSkift={skift} bredde="w-36" />
          </div>
          <div>
            <Felt etiket="Ekskl. moms" sti="subtotal" vaerdi={v('subtotal')} redigerbar={kanRette} onSkift={skift} bredde="w-32" suffiks={d.currency || ''} />
            <Felt etiket="Moms"        sti="vat"      vaerdi={v('vat')}      redigerbar={kanRette} onSkift={skift} bredde="w-32" suffiks={d.currency || ''} />
            <Felt etiket="I alt"       sti="total"    vaerdi={v('total')}    redigerbar={kanRette} onSkift={skift} bredde="w-32" suffiks={d.currency || ''} />
            <Felt etiket="Reg./konto"  sti="payment.bank_reg_number" vaerdi={v('payment.bank_reg_number')} redigerbar={kanRette} onSkift={skift} bredde="w-24" />
            <Felt etiket="Kontonr."    sti="payment.bank_account"    vaerdi={v('payment.bank_account')}    redigerbar={kanRette} onSkift={skift} bredde="w-40" />
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap mt-2 pt-2 border-t border-th-border-light">
        {!gammel && (
          <Button size="sm" variant="ghost" onClick={sammenlign} disabled={!!arbejder}>
            {arbejder === 'sammenlign'
              ? <Loader2 size={13} className="animate-spin mr-1" />
              : <GitCompare size={13} className="mr-1" />}
            Sammenlign med gammel udlæser
          </Button>
        )}
        {gammel && (
          <button onClick={() => setGammel(null)}
                  className="text-xs text-th-text-faint hover:text-th-text underline">
            skjul sammenligning
          </button>
        )}
      </div>

      {gammel && <Sammenligning svar={gammel} nyData={d} kanRette={kanRette} onOvertag={skift} />}

      {(d.line_items || []).length > 0 && (
        <div className="mt-2">
          <button onClick={() => setVisLinjer(x => !x)}
                  className="flex items-center gap-1 text-xs text-th-text-muted hover:text-th-text">
            {visLinjer ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            {d.line_items.length} varelinjer
          </button>
          {visLinjer && (
            <div className="overflow-x-auto mt-1">
              <table className="text-xs w-full min-w-[420px]">
                <tbody>
                  {d.line_items.map((l, i) => (
                    <tr key={i} className="border-t border-th-border-light">
                      <td className="py-1 pr-2 text-th-text-secondary">{l.description}</td>
                      <td className="py-1 px-2 text-right text-th-text-muted whitespace-nowrap">{l.quantity}</td>
                      <td className="py-1 px-2 text-right text-th-text-muted whitespace-nowrap">{visTal(l.unit_price)}</td>
                      <td className="py-1 pl-2 text-right text-th-text whitespace-nowrap">{visTal(l.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {issues.length > 0 && (
        <div className="mt-3 space-y-1">
          {issues.map((i, n) => (
            <div key={n} className="flex items-start gap-2 text-xs">
              <AlertTriangle size={13} className={i.severity === 'error' ? 'text-red-600 mt-0.5 shrink-0' : 'text-amber-600 mt-0.5 shrink-0'} />
              <span className="text-th-text-secondary">
                {i.message || i.code}
                {i.code === 'SUPPLIER_UNMATCHED' && (
                  <span className="text-th-text-faint"> — leverandøren oprettes automatisk, når du godkender.</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {kanRette && (
        <>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Button size="sm" variant="outline" onClick={gem}
                    disabled={!harRettelser || !!arbejder}>
              {arbejder === 'gem' ? <Loader2 size={13} className="animate-spin mr-1" /> : null}
              Gem rettelser
            </Button>
            <Button size="sm" onClick={godkend} disabled={!!arbejder}>
              {arbejder === 'godkend' ? <Loader2 size={13} className="animate-spin mr-1" /> : <Check size={13} className="mr-1" />}
              Godkend
            </Button>
            <Button size="sm" variant="ghost" onClick={afvis} disabled={!!arbejder}>
              <X size={13} className="mr-1" /> Afvis
            </Button>
          </div>
          {/* Platformen revaliderer ikke ved godkendelse. Det skal stå her,
              hvor fingeren er på knappen — ikke kun i en plan. */}
          <p className="text-xs text-th-text-faint mt-2">
            Det, der står her, bliver leveret som det er. Udlæsningen kontrollerer det ikke igen.
          </p>
        </>
      )}

      {(u.fa_spor || []).length > 0 && (
        <p className="text-xs text-th-text-faint mt-2">
          {u.fa_spor[u.fa_spor.length - 1].handling} af {u.fa_spor[u.fa_spor.length - 1].af}
        </p>
      )}
    </div>
  );
}
